<template>
    <div
        class="input-outer"
        :class="{
            [mode]: true,
            disabled: !!disabled,
        }"
    >
        <div class="tip-wrap" v-if="isUseTip">
            <q-tooltip
                anchor="top middle"
                self="bottom middle"
                class="triangle"
                v-model="isTipVisible"
            >
                {{ sTipWords }}
            </q-tooltip>
        </div>
        <!-- ▲公共提示部分 -->
        <!-- ▼小输入框 -->
        <fieldset
            class="input-body"
            :class="{ 'has-label': label }"
            v-if="mode == 'normal'"
            @click="toFocus"
        >
            <i class="prefix" v-if="prefix">
                {{ prefix }}
            </i>
            <i class="suffix" v-if="suffix">
                {{ suffix }}
            </i>
            <div class="input-wrap">
                <input
                    type="text"
                    ref="oInput"
                    v-model="sInputVal"
                    :min="min"
                    :max="max"
                    :inputmode="inputmode"
                    :placeholder="placeholder"
                    :maxlength="maxlength"
                    @blur="handleBlur"
                    @focus="() => setBlue(true)"
                />
                <ButtonGrayButton :hide="['right']" bald :blue="isBlue" v-if="label">
                    {{ label }}
                </ButtonGrayButton>
            </div>
        </fieldset>
        <!-- 
            ▲小输入框 | ▼大输入框
            两者互斥
        -->
        <fieldset
            class="input-body"
            :class="{ 'focused-within': isFocused }"
            v-if="mode == 'multiple'"
            @click="toFocus"
        >
            <div class="icon" v-if="icon">
                <!-- ↓ 有 length 说明是字符型 -->
                <CurrentCoin size="20px" :name="icon.length ? icon : ''" />
            </div>
            <div class="middle-part">
                <label class="label select-none">
                    <span>{{ label }}</span>
                    <span>{{ label02 }}</span>
                </label>
                <span class="input-wrap" :class="{ hasPrefix: prefix }">
                    <span class="input" v-if="1">
                        <i v-if="prefix" class="prefix">{{ prefix }}</i>
                        <em>{{ sInputVal ?? '&nbsp;&nbsp;' }}</em>
                        <i v-if="suffix" class="suffix">{{ suffix }}</i>
                    </span>
                    <!-- ▼ 因为使用了 type="text" -->
                    <!-- :step="inputmode == 'numeric' ? 1 : 0.00000001" -->
                    <input
                        type="text"
                        ref="oInput"
                        v-model="sInputVal"
                        :min="min"
                        :max="max"
                        :inputmode="inputmode"
                        :placeholder="placeholder"
                        :maxlength="maxlength"
                        @blur="handleBlur"
                        @focus="() => (isFocused = true)"
                        :disabled="!!disabled"
                    />
                    <!-- inputmode="decimal/numeric" -->
                </span>
            </div>
            <!--  -->
            <button class="right-part"
                v-if="showButton"
                :class="{ inputed: modelValue > 0 }"
                @click="setInfitity"
            >
                <svg fill="none"
                    viewBox="0 0 18 10"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path fill="currentColor"
                        d="M13.2.8c-1.8 0-3 1.3-4.2 2.8C7.8 2.1 6.6.8 4.8.8a4.2 4.2 0 0 0 0 8.4c1.8 0 3-1.3 4.2-2.8 1.2 1.5 2.4 2.8 4.2 2.8a4.2 4.2 0 0 0 0-8.4ZM4.8 7.5v.8-.8a2.5 2.5 0 0 1-2.4-2 2.5 2.5 0 0 1 2.4-3C6 2.5 7 3.7 8 5 7 6.3 6 7.5 4.8 7.5Zm8.4 0C12 7.5 11 6.3 10 5c1-1.3 2-2.5 3.2-2.5a2.5 2.5 0 0 1 0 5Z"
                    ></path>
                </svg>
            </button>
        </fieldset>
    </div>
</template>

<script setup>
import { transferToNumber } from '~/common/js/pure-functions';

const emit = defineEmits(['update:modelValue', 'hasFocused']);

const props = defineProps({
    disabled: Boolean, // 禁用效果
    modelValue: {
        // type: Number,
        type: [Number, String],
        default: 0,
        required: true,
    },
    inputmode: {
        type: String,
        default: 'decimal', // 默认小数
        validator(value) {
            // numeric = 整数 ● decimal = 小数
            return ['numeric', 'decimal'].includes(value);
        },
    },
    icon: {
        // 值 === true 表示显示当前币种，
        // 值为 String 表示指定具体的货币图标（待开发）
        type: [Boolean, String],
    },
    showButton: { type: Boolean }, // 显示尾部按钮
    prefix: { type: String }, //前缀
    suffix: { type: String }, //后缀
    label: { type: String }, //  标题1
    label02: { type: String }, // 标题2
    min: { type: Number }, // 数字下限
    max: { type: Number }, // 数字上限
    tailLong: {
        // 传了[2,8] 表示控制小数点最少2位，最多8位
        type: [Array],
        // 默认保留2位小数（仅在 decimal 即小数模式时生效）
        default: () => [2, 2],
    },
    placeholder: { type: String }, // html 原生属性
    maxlength: {
        // html 原生属性
        type: [Number, String],
        default: 10,
    },
    tip: {
        type: Object,
        default: () => ({}),
        validator(value) {
            const sKeys = Object.keys(value);
            const aList = ['min', 'max'];
            return sKeys.every((cur) => aList.includes(cur));
        },
    },
    mode: {
        // normal =  单选模式， multiple = 多行模式
        type: String,
        default: 'normal',
        validator(value) {
            return ['normal', 'multiple'].includes(value);
        },
    },
});

let submited = null;
const oInput = ref(null);
const sInputVal = ref(props.modelValue || '');
const isTipVisible = ref(false);
const sTipWords = ref('');
const isFocused = ref(false);
const isBlue = ref(false);
const ReExp = computed(() => {
    if (props.inputmode === 'numeric') return /^\d*$/; // [1-9]
    return /^\d*(\.\d{0,20})?$/;
});
const aRuleToFix = computed(() => {
    // 整数模式不要小数点
    if (props.inputmode === 'numeric') return [0, 0];
    return props.tailLong;
});

const isUseTip = computed(() => {
    const minTip = isNumber(props.min) && props.tip.min;
    const maxTip = isNumber(props.max) && props.tip.max;
    return minTip || maxTip;
});
const isInteger = computed(() => {
    return props.inputmode === 'numeric';
});

// =============== ↑ 以上是单纯的声明动作 ======================================
// =============== ↓ 具体操作在下方 ============================================

// ↓ 监听上层下发值
watch(
    () => props.modelValue,
    (iNew, iOld) => {
        if (iNew === submited) return;
        if (iNew === 0 && submited === void 0) return;
        let sResult;
        if (iNew === '') {
            sResult = '';
        } else {
            sResult = transferToNumber(iNew, ...aRuleToFix.value);
        }
        sInputVal.value = sResult;
        // console.log("下发的：旧|新|转换结果->", iOld, '|', iNew, '|', sResult);
    },
    {
        immediate: true,
    },
);

// ↓ 监听自身修改值
watch(sInputVal, (sNew, sOld) => {
    console.log('自有变量：旧|新->', sOld, '|', sNew);
    sNew ??= '';
    let { selectionStart, selectionEnd } = oInput.value;
    // console.log('光标位置', selectionStart, selectionEnd);
    // console.log(`校验结果 ${ReExp.value.test(sNew)}`, ReExp.value.toString());
    if (sNew === '') {
        toSubmitValue(void 0);
        return;
    } else if (ReExp.value.test(sNew) === false) {
        toSubmitValue(sOld * 1);
        setShowingValue(sOld);
        if (sOld === '.') selectionStart = 999; // 处理一下特殊情况
        nextTick(() => {
            oInput.value.setSelectionRange(selectionStart - 1, selectionStart - 1);
        });
        return;
    }
    // ▼至此表示输入为合法数字
    toSubmitValue(sNew * 1);
    setShowingValue(sNew);
});

function setInfitity() {
    toSubmitValue(0);
    setShowingValue('0');
}

function checkNumber(theVal) {
    if (!isUseTip.value) return;
    const { min, max, tip } = props;
    const minTip = isNumber(min) && tip.min;
    const maxTip = isNumber(max) && tip.max;
    const samllOrEmtpy = theVal < min || sInputVal.value === '';
    if (minTip && samllOrEmtpy) {
        isTipVisible.value = true;
        sTipWords.value = tip.min.replace('●', min);
    } else if (maxTip && theVal > max) {
        isTipVisible.value = true;
        sTipWords.value = tip.max.replace('●', max);
    } else {
        isTipVisible.value = false;
    }
}

function toSubmitValue(newVal) {
    submited = newVal; // 提交之前自已记录一下
    emit('update:modelValue', newVal);
    checkNumber(newVal);
}

function setShowingValue(newVal) {
    sInputVal.value = newVal;
    if (oInput.value){
        oInput.value.value = newVal;
    }
}

// ↓ 处理失焦事件
function handleBlur() {
    isFocused.value = false;
    let newVal = oInput.value?.value;
    const { modelValue, min, max } = props;
    const hasMinVal = isNumber(min);
    if (newVal === '') {
        if (hasMinVal) {
            newVal = min; // 按最新讨论结果，用户0余额时也给一个默认值"最小下注金额”
        } else {
            return; // 后补操作
        }
    } else if (modelValue < min && hasMinVal) {
        newVal = min;
    } else if (modelValue > max && isNumber(max)) {
        newVal = max;
    }
    if (isInteger.value) {
        // 整数
        if (newVal) newVal *= 1; // 消除0开头
    } else {
        // 小数
        newVal = transferToNumber(newVal, ...aRuleToFix.value); //
    }
    if (newVal !== '') {
        toSubmitValue(newVal * 1);
        setShowingValue(newVal);
        // console.log('blur 赋值');
        return;
    }
    return console.log('blur 跳过');
}

function toFocus() {
    oInput.value.select();
    setBlue(true);
    emit('hasFocused'); // 报告父组件已经聚焦了
}

function setBlue(bNewVal) {
    if (bNewVal === true || bNewVal === false) {
        isBlue.value = bNewVal;
    } else {
        isBlue.value = !isBlue.value;
    }
}

function isNumber(value) {
    // 使用typeof检查值是否是数字类型，并且使用isNaN检查是否不是NaN
    return typeof value === 'number' && !isNaN(value);
}

defineExpose({
    isBlue, // 用于父级组件修改此值
    toFocus,
});
</script>

<style lang="scss" scoped>
.input-outer {
    &[class~='disabled'] > * {
        pointer-events: none;
    }
}

.normal {
    @import './style/normal-input.scss';
}

.multiple {
    @import './style/multiple-lines.scss';
}

// ------------------------------------
.tip-wrap {
    width: 100%;
    height: 0px;
}
</style>



